import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Cart AJAX fetch：在 fetchConfig('json') 基础上追加 X-Requested-With 头。
 * 让购物车 POST 更接近标准 AJAX 请求，降低被安全层误判的概率。
 * @param {string} url
 * @param {string} body - JSON 字符串。
 */
function cartFetch(url, body) {
  const config = fetchConfig('json', { body });
  return fetch(url, {
    ...config,
    headers: {
      .../** @type {Record<string, string>} */ (config.headers),
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleUpgradeClick);
    this.addEventListener('change', this.#handleSellingPlanSelectChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleUpgradeClick);
    this.removeEventListener('change', this.#handleSellingPlanSelectChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const itemKey = lineItemRow?.dataset.key;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      itemKey,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.itemKey] - The cart item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.updateLine({
      line: config.line,
      itemKey: config.itemKey,
      quantity: config.quantity,
      action: config.action,
    });
  }

  /**
   * Handles selling plan select changes in the cart drawer.
   * @param {{line?: number}} data
   * @param {Event} event
   */
  onSellingPlanSelect(data, event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!data.line) return;

    const row = /** @type {HTMLElement | null} */ (event.target.closest('.cart-line-card-row[data-key]'));

    this.updateLine({
      line: data.line,
      variantId: row?.dataset.variantId,
      quantity: this.#getLineQuantity(data.line),
      selling_plan: event.target.value,
      action: 'selling-plan',
    });
  }

  /**
   * Handles selling plan changes after cart section re-renders.
   * @param {Event} event - The change event.
   */
  #handleSellingPlanSelectChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('.cart-line-card__plan-select')) return;

    const row = /** @type {HTMLElement | null} */ (event.target.closest('.cart-line-card-row[data-line]'));
    const line = Number.parseInt(row?.dataset.line || '', 10);
    if (!line) return;

    this.updateLine({
      line,
      variantId: row?.dataset.variantId,
      quantity: this.#getLineQuantity(line),
      selling_plan: event.target.value,
      action: 'selling-plan',
    });
  };

  /**
   * Handles upgrade buttons that are restored after cart section morphing.
   * @param {MouseEvent} event - The click event.
   */
  #handleUpgradeClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-cart-upgrade-selling-plan]');
    if (!(button instanceof HTMLButtonElement)) return;

    const line = Number.parseInt(button.dataset.cartLine || '', 10);
    const sellingPlan = button.dataset.cartUpgradeSellingPlan;
    if (!line || !sellingPlan) return;

    const row = /** @type {HTMLElement | null} */ (button.closest('.cart-line-card-row[data-key]'));

    event.preventDefault();
    this.updateLine({
      line,
      variantId: row?.dataset.variantId,
      quantity: this.#getLineQuantity(line),
      selling_plan: sellingPlan,
      action: 'selling-plan',
    });
  };

  /**
   * Handles one-click upgrades to the first subscription plan.
   * @param {{line?: number, selling_plan?: string | number}} data
   */
  onSellingPlanChange(data) {
    if (!data.line || data.selling_plan === undefined) return;

    const row = /** @type {HTMLElement | null} */ (this.querySelector(`.cart-line-card-row[data-line="${data.line}"]`));

    this.updateLine({
      line: data.line,
      variantId: row?.dataset.variantId,
      quantity: this.#getLineQuantity(data.line),
      selling_plan: data.selling_plan.toString(),
      action: 'selling-plan',
    });
  }

  /**
   * Gets the current visible quantity for a cart line before changing its selling plan.
   * Shopify may rebuild a line when adding/removing selling_plan, so we submit quantity too.
   * @param {number} line - The line item index.
   * @returns {number | undefined}
   */
  #getLineQuantity(line) {
    const lineItemRow = this.#findLineRow(line);
    const input = lineItemRow?.querySelector('input[name="updates[]"]');
    if (!(input instanceof HTMLInputElement)) return undefined;

    const quantity = Number.parseInt(input.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : undefined;
  }

  /**
   * @param {number} line
   * @returns {Element | null | undefined}
   */
  #findLineRow(line) {
    return this.querySelector(`.cart-line-card-row[data-line="${line}"]`)
      || this.refs.cartItemRows?.[line - 1];
  }


  /**
   * Updates a cart line through Shopify's cart change endpoint.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.variantId] - Variant id. Required when switching subscription to one-time.
   * @param {number} [config.quantity] - The new quantity.
   * @param {string | null} [config.selling_plan] - The new selling plan id, or null to remove it.
   * @param {string} config.action - The action.
   */
  updateLine(config) {
    if (config.action === 'selling-plan') {
      this.#changeSellingPlan(config);
      return;
    }

    this.#changeQuantity(config);
  }

  /**
   * Changes only the quantity for a cart line.
   * @param {{line: number, itemKey?: string, quantity?: number, action: string}} config
   */
  #changeQuantity(config) {
    const { line, itemKey, quantity } = config;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();
    const { cartTotal } = this.refs;

    const body = JSON.stringify({
      ...(itemKey ? { id: itemKey } : { line }),
      ...(quantity !== undefined ? { quantity } : {}),
      sections: this.#sectionsParam(),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    cartFetch(Theme.routes.cart_change_url, body)
      .then((response) => this.#parseCartResponse(response, 'change quantity'))
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#updateQuantitySelectors(parsedResponseText);
        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: parsedResponseText.item_count ?? 0,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        return this.#applySectionHTML(parsedResponseText.sections);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Changes a cart line selling plan through the replacement-line path.
   * @param {{line: number, quantity?: number, variantId?: string, selling_plan?: string | null, action: string}} config
   */
  #changeSellingPlan(config) {
    if (!config.variantId) return;

    // Shopify /cart/change.js 不稳定支持 selling_plan 字段。任何 selling_plan
    // 切换（含切回一次性、切到任意订阅 plan）都走 remove + add 两步。
    this.#replaceLineWithPlan({
      line: config.line,
      quantity: config.quantity,
      variantId: config.variantId,
      sellingPlan: config.selling_plan === '' ? null : config.selling_plan,
      action: config.action,
    });
  }

  /**
   * 替换 cart line 的 selling_plan：先删除当前 line，再用目标 selling_plan add。
   * 适用于 一次性→订阅、订阅→一次性、订阅→不同频率。
   *
   * @param {{ line: number, quantity?: number, variantId: string, sellingPlan: string | null | undefined, action: string }} config
   */
  async #replaceLineWithPlan(config) {
    const { line, quantity, variantId, sellingPlan, action } = config;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const sections = this.#sectionsParam();
    const sectionsUrl = window.location.pathname;

    try {
      const removeResponse = await cartFetch(Theme.routes.cart_change_url, JSON.stringify({
        line,
        quantity: 0,
        sections,
        sections_url: sectionsUrl,
      }));
      await this.#parseCartResponse(removeResponse, 'remove line');

      const addBody = {
        id: Number(variantId),
        quantity: quantity ?? 1,
        sections,
        sections_url: sectionsUrl,
      };
      if (sellingPlan) {
        /** @type {any} */ (addBody).selling_plan = sellingPlan;
      }

      const addResponse = await cartFetch(Theme.routes.cart_add_url, JSON.stringify(addBody));
      const addData = await this.#parseCartResponse(addResponse, 're-add line');

      resetShimmer(this);

      if (addData.status >= 400 || addData.errors) {
        this.#handleCartError(line, addData);
        return;
      }

      this.dispatchEvent(
        new CartUpdateEvent(this.#shapeCartAddResponse(addData, variantId, quantity), this.sectionId, {
          itemCount: addData.item_count,
          source: 'cart-items-component',
          sections: addData.sections,
        })
      );

      await this.#applySectionHTML(addData.sections);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Parses a cart API response and produces a readable error for HTML challenges.
   * @param {Response} response - Fetch response.
   * @param {string} context - Operation name.
   * @returns {Promise<any>}
   */
  async #parseCartResponse(response, context) {
    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`[Cart] ${context} failed: ${response.status} ${responseText.slice(0, 300)}`);
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      throw new Error(`[Cart] ${context} returned non-JSON: ${responseText.slice(0, 300)}`);
    }
  }

  /**
   * Adds the minimum item shape expected by cart update listeners.
   * @param {any} addData - Cart add response.
   * @param {string} variantId - Variant id.
   * @param {number | undefined} quantity - Requested quantity.
   * @returns {any}
   */
  #shapeCartAddResponse(addData, variantId, quantity) {
    return {
      ...addData,
      // 避免 product-form.#onCartUpdate 因为缺 resource.items 而再发 GET /cart.js。
      items: [{ variant_id: Number(variantId), quantity: addData.quantity ?? quantity ?? 1 }],
    };
  }

  /**
   * Build the comma-separated section list expected by the cart API's `sections` param.
   * @returns {string}
   */
  #sectionsParam() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate).join(',');
  }

  /**
   * Morph the current section from the inline `sections` map returned by the cart API.
   * Falls back to a fresh fetch only when the response doesn't contain our section.
   *
   * @param {Record<string, string> | undefined} sections
   * @param {{ mode?: 'full' | 'hydration' }} [options]
   */
  async #applySectionHTML(sections, { mode = 'full' } = {}) {
    const inlineHTML = sections?.[this.sectionId];
    if (inlineHTML) {
      await morphSection(this.sectionId, inlineHTML, { mode });
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
    }
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      console.warn('[Cart] Selling plan change error:', parsedResponseText.errors);
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#renderSectionsFromEvent(event, { mode: 'full' });
      return;
    }
    if (event.target === this) return;

    this.#renderSectionsFromEvent(event, { mode: 'full' });
  };

  /**
   * Prefer inline sections HTML attached to the dispatched event.
   *
   * @param {Event} event
   * @param {{ mode: 'full'|'hydration' }} options
   */
  #renderSectionsFromEvent(event, { mode }) {
    const detail = /** @type {any} */ (event).detail;
    const eventSections =
      detail?.data?.sections ?? detail?.resource?.sections ?? detail?.sections;
    this.#applySectionHTML(eventSections, { mode }).catch((error) => console.error(error));
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
